import { useQueryClient, useMutation } from "@tanstack/react-query"
import { getCookie } from "api/apiUtils"
import PropTypes from "prop-types"
import { ACCESS_TOKEN_KEY } from "constants/appState.js"
import useAxiosPrivate from "./useAxiosPrivate"

// hooks
import useAppState from "context/hooks/useAppState"

const useMutateApi = ({
  method,
  apiEndpoint,
  invalidateQueries = [],
  successMsg,
  infoMsg,
  options = {}, // axios options
}) => {
  const axiosPrivate = useAxiosPrivate()
  const queryClient = useQueryClient()
  const { openToastNotification } = useAppState()

  const { mutate } = useMutation({
    mutationFn: async ({ id, body, headers = {}, params = {} }) => {
      let url = apiEndpoint
      if (id) url = `${apiEndpoint}${id}`

      let axiosParams = {
        method,
        url,
        headers: {
          Authorization: `Bearer ${getCookie(ACCESS_TOKEN_KEY)}`,
          "Cache-Control": "no-store, no-cache, must-revalidate, max-age=0",
          ...headers,
        },
        credentials: "include",
        options,
        params,
      }
      if (body) axiosParams.data = body
      return await axiosPrivate(axiosParams)
    },
    onSuccess: (data) => {
      invalidateQueries.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key })
      })

      if (successMsg) {
        if (typeof successMsg === "string") {
          openToastNotification("success", "Success", successMsg)
        } else if (typeof successMsg === "object") {
          if (successMsg.description) {
            const placement = successMsg.placement || "topRight"
            openToastNotification("success", "Success", successMsg.description, placement)
          } else {
            console.log("useMutateApi: if successMsg prop sent as an object, it must contain a description key.")
          }
        }
      }
      if (infoMsg) openToastNotification("info", "Info", infoMsg)

      return data
    },
    onError: (err) => {
      console.log("useMutateApi err", { err })
      return err
    },
  })

  return mutate
}

useMutateApi.propTypes = {
  props: PropTypes.shape({
    method: PropTypes.oneOf(["post", "put", "delete"]).isRequired,
    apiEnpoint: PropTypes.string.isRequired.isRequired,
    invalidateQueries: PropTypes.array,
    successMsg: PropTypes.oneOf([
      PropTypes.string,
      PropTypes.shape({
        description: PropTypes.string.isRequired,
        placement: PropTypes.oneOf(["top", "topLeft", "topRight", "bottom", "bottomLeft", "bottomRight"]),
      }),
    ]),
    infoMsg: PropTypes.string, // a node can also be sent
  }).isRequired,
}

export default useMutateApi
